import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from "react";
import './datePickerStyle.scss';
import { DateTime } from "luxon";
import Tooltip from "../tooltips/Tooltip";
import { useTranslation } from "react-i18next";
import BaseIconButton from "../buttons/iconButtons/BaseIconButton";
import { MultiplyIcon } from "../../libs/corporate/icons/outlined/suggested/SymbolCollection";

interface IDatePickerProps {
    date?: DateTime | null
    onChange?: (value: DateTime | null) => void
    onBlur?: (value: DateTime | null) => void
    onFocus?: () => void
    incorrectPeriod?: boolean
    disabled?: boolean
    isModal?: boolean
    maxDate?: string | number | null
    minDate?: string | number | null
    inputStyle?: CSSProperties
    clearButtonStyle?: CSSProperties
    preventFutureDates ?: boolean
    maxAllowedDate?: DateTime | null
}

const TIME_SHOW_ERROR = 1000


export const DatePicker: FC<IDatePickerProps> = (props) => {
    const { t } = useTranslation();
    const { 
        preventFutureDates, 
        onChange, 
        maxAllowedDate: propMaxAllowedDate,
        date        
    } = props;

    useEffect(() => {
        try {
            setValue(date?.toFormat("yyyy-MM-dd"))
        } catch(ex) {
            console.warn("ex", ex)
        }
    }, [date])

    const [value, setValue] = useState<string |  undefined>(date?.isValid === true ? date?.toFormat("yyyy-MM-dd") : '');
    const ref = useRef<HTMLInputElement>(null);
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const [showFutureDateError, setShowFutureDateError] = useState<boolean>(false);
    const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null);

    const getMaxAllowedDate = useCallback((): DateTime => {
        return propMaxAllowedDate || DateTime.now();
    }, [propMaxAllowedDate]);

    const validate = (date: DateTime): DateTime => {
        let minDate = DateTime.fromJSDate(new Date(props.minDate as string));
        let maxDate = DateTime.fromJSDate(new Date(props.maxDate as string));
        const maxAllowedDate = getMaxAllowedDate();

        if (preventFutureDates && date > maxAllowedDate) {
            setShowFutureDateError(true);
            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }
            const timeout = setTimeout(() => setShowFutureDateError(false), TIME_SHOW_ERROR);
            setErrorTimeout(timeout);
            return maxAllowedDate;
        }

        if (props.minDate && date <= minDate) {
            return minDate
        } else if ( props.maxDate && date >= maxDate) {
            return maxDate
        } else {
            return date
        }
    }

    useEffect(() => {
        if (value && preventFutureDates) {
            const selectedDate = DateTime.fromJSDate(new Date(value));
            const maxAllowedDate = getMaxAllowedDate();

            if (selectedDate.isValid && selectedDate > maxAllowedDate) {
                setShowFutureDateError(true);
                if (errorTimeout) {
                    clearTimeout(errorTimeout);
                }
                const timeout = setTimeout(() => setShowFutureDateError(false), TIME_SHOW_ERROR);
                setErrorTimeout(timeout);

                const correctedDate = maxAllowedDate.toFormat("yyyy-MM-dd");
                setValue(correctedDate);
                onChange?.(maxAllowedDate);
            }
        }
    }, [
        value, 
        preventFutureDates, 
        getMaxAllowedDate, 
        errorTimeout, 
        onChange
    ]);

    useEffect(() => {
        return () => {
            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }
        };
    }, [errorTimeout]);

    return (
        <>
            <div className="datePicker">
                <Tooltip title={t("general.dateSelection")}>
                    <input
                        className={showFutureDateError ? 'error' : ''}
                        ref={ref}
                        type="date"
                        style={props.inputStyle}
                        onSelect={(date) => {
                            const dateString = date.currentTarget.value;
                            setValue(dateString);

                            if (dateString) {
                                const newDate = DateTime.fromISO(dateString);
                                if (newDate.isValid) {
                                    const validatedDate = validate(newDate);
                                    onChange?.(validatedDate);
                                }
                            } else {
                                onChange?.(null);
                            }
                        }}
                        onChange={(date) => {
                            const dateString = date.currentTarget.value;

                            if (isFirefox) {
                                if (dateString !== '') {
                                    setValue(dateString);
                                    onChange?.(DateTime.fromJSDate(new Date(dateString)));
                                } else {
                                    setValue(value);
                                    onChange?.(DateTime.fromJSDate(new Date(value as string || '')));
                                }
                            } else {
                                setValue(dateString);

                                if (dateString) {
                                    const newDate = DateTime.fromJSDate(new Date(dateString));
                                    if (newDate.isValid) {
                                        const validatedDate = validate(newDate);
                                        onChange?.(validatedDate);
                                    }
                                } else {
                                    onChange?.(null);
                                }
                            }
                        }}
                        onBlur={(date) => {
                            if (typeof date === "string") return;

                            let _date = date.currentTarget.value
                            let result = date ? validate(DateTime.fromJSDate(new Date(_date))).toFormat("yyyy-MM-dd") : _date
                            if (date.currentTarget.value.length > 10) {
                                setValue(props.maxDate as string);
                                props.onBlur?.(DateTime.fromJSDate(new Date(props.maxDate as string)));
                            } else {
                                if (props.date?.toFormat("yyyy-MM-dd") === _date) {
                                    date.preventDefault();
                                } else {
                                    if (_date) {
                                        const newDate = DateTime.fromJSDate(new Date(_date));
                                        if (newDate.isValid) {
                                            const validatedDate = validate(newDate);
                                            const formattedDate = validatedDate.toFormat("yyyy-MM-dd");

                                            if (formattedDate !== _date) {
                                                setValue(formattedDate);
                                                props.onBlur?.(validatedDate);
                                            } else {
                                                props.incorrectPeriod ? setValue(props.date?.toFormat("yyyy-MM-dd")) : setValue(result);
                                                props.onBlur?.(DateTime.fromJSDate(new Date(result === undefined ? "" : result)));
                                            }
                                        }
                                    } else {
                                        props.incorrectPeriod ? setValue(props.date?.toFormat("yyyy-MM-dd")) : setValue(result);
                                        props.onBlur?.(DateTime.fromJSDate(new Date(result === undefined ? "" : result)));
                                    }
                                }
                            }
                        }}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape') && ref.current?.blur()}
                        value={value}
                        disabled={props.disabled}
                        max={props.maxDate ? props.maxDate : undefined}
                        min={props.minDate ? props.minDate : undefined}
                        onFocus={props.onFocus}
                    />
                </Tooltip>
                <div
                    style={props.clearButtonStyle}
                    className="dateClearButton"
                >
                    <Tooltip title={t("general.clear")}>
                        <BaseIconButton
                            tabIndex={-1}
                            sizeVariant={props?.clearButtonStyle?.height === 30 ? 'micro' : 'mini'}
                            onClick={() => {
                                setValue('');
                                props.onBlur?.(DateTime.fromJSDate(new Date('')));
                            }}
                            {...props}
                        >
                            <MultiplyIcon />
                        </BaseIconButton>
                    </Tooltip>
                </div>
            </div>
            {showFutureDateError && (
                <div className="datePicker-error">
                    {t("general.futureDateNotAllowed")}
                </div>
            )}
        </>        
    )
}

